import { useEffect, useRef } from 'react'

import type { Api_Video } from '@core/api'
import { useGlobalStore } from '@core/hooks/useGlobalStore'
import { logError } from '@core/logging'
import {
  shoppingActionsSelector,
  widgetProductsLoadedCallbackSelector,
} from '@core/shopping/model'
import type { IAllWidgetParams } from '@core/types'

interface Props {
  videos: Api_Video[]
  params: IAllWidgetParams
}

/**
 * Triggers the widgetProductsLoadedCallback each time a new page of videos
 * arrives for a widget (initial fetch and each loadMore). Products returned
 * by the callback are stored in the global productsHydrated map.
 */
export function useWidgetProductsHydration({ videos, params }: Props): void {
  const widgetProductsLoadedCallback = useGlobalStore(
    widgetProductsLoadedCallbackSelector,
  )
  const { hydrateProducts } = useGlobalStore(shoppingActionsSelector)
  const lastVideoCount = useRef(0)

  useEffect(() => {
    if (
      !widgetProductsLoadedCallback ||
      !videos.length ||
      videos.length <= lastVideoCount.current
    ) {
      return
    }

    lastVideoCount.current = videos.length

    const seen = new Set<string>()
    const products = videos
      .flatMap((video) => video.products ?? [])
      .filter((p) => {
        const key = p.product_ext_id
        if (seen.has(key)) {
          return false
        }
        seen.add(key)
        return true
      })
    if (!products.length) {
      return
    }

    widgetProductsLoadedCallback({
      widgetId: params.widget_id ?? '',
      name: params.name ?? '',
      videos,
      products,
    })
      .then((hydratedProducts) => {
        if (hydratedProducts) {
          hydrateProducts(hydratedProducts)
        }
      })
      .catch((e) => {
        logError(e)
      })
  }, [videos, widgetProductsLoadedCallback, hydrateProducts, params])
}
